import { useEffect, useMemo, useState } from 'react';
import { auth } from '@/lib/firebase';
import { useIsMobile } from '@/hooks/useIsMobile';

/** Saved orders this session, so a form that remounts gets its order back right away. */
const memoryCache = new Map<string, string[]>();

function readSaved<Id extends string>(storageKey: string, defaultOrder: readonly Id[]): Id[] | null {
  const valid = (ids: unknown): Id[] | null =>
    Array.isArray(ids)
      ? ids.filter((id): id is Id => typeof id === 'string' && (defaultOrder as readonly string[]).includes(id))
      : null;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return valid(JSON.parse(raw));
  } catch {
    // Storage unavailable: fall back to this session's copy.
  }
  return valid(memoryCache.get(storageKey) ?? null);
}

/**
 * A form's box order that each user can rearrange ("Reorder" mode). Saved in this browser per
 * signed-in user, with phones and desktop kept separate since their layouts differ.
 *
 * @param formKey  storage name for the form, e.g. "newJob"
 * @param defaultOrder  every box id in the default order
 * @param isOpen  the form's open state; Reorder mode switches off and the order is re-read each time it opens
 */
export function useSectionOrder<Id extends string>(formKey: string, defaultOrder: readonly Id[], isOpen: boolean) {
  const isMobile = useIsMobile();
  // Read straight from Firebase: the app only shows these forms once sign-in is restored, so the
  // user is already known here (no "anonymous first, then the user" switch).
  const uid = auth.currentUser?.uid ?? 'anonymous';
  const storageKey = `${formKey}SectionOrder:${isMobile ? 'mobile:' : ''}${uid}`;

  const [saved, setSaved] = useState<Id[] | null>(() => readSaved(storageKey, defaultOrder));
  const [isReordering, setIsReordering] = useState(false);

  // Re-read when the key changes (another user, phone/desktop) and every time the form opens.
  useEffect(() => {
    setSaved(readSaved(storageKey, defaultOrder));
    if (isOpen) setIsReordering(false);
    // defaultOrder is a module constant in every caller
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey, isOpen]);

  const order = useMemo<Id[]>(() => {
    const kept = saved ?? [];
    return [...kept, ...defaultOrder.filter((id) => !kept.includes(id))];
  }, [saved, defaultOrder]);

  const save = (ids: Id[] | null) => {
    setSaved(ids);
    if (ids) memoryCache.set(storageKey, ids);
    else memoryCache.delete(storageKey);
    try {
      if (ids) localStorage.setItem(storageKey, JSON.stringify(ids));
      else localStorage.removeItem(storageKey);
    } catch {
      // Storage unavailable (private mode): the session copy above still applies.
    }
  };

  return {
    /** Every box id in the user's order (hidden boxes included). */
    order,
    /** True once the user has saved their own order. */
    isCustom: saved !== null && saved.length > 0,
    /** Save the visible boxes' new order; hidden boxes keep their place after them. */
    reorder: (visibleIds: string[]) => {
      const moved = visibleIds as Id[];
      save([...moved, ...order.filter((id) => !moved.includes(id))]);
    },
    reset: () => save(null),
    isReordering,
    setIsReordering,
    isMobile,
  };
}
